import { App, CDN, CONFIG, DOMAIN } from 'config';
import { t } from 'zf-web-components';
import type { LocalStore } from 'zf-web-infra';

export class ThemeService {

  private readonly local: LocalStore;
  constructor(local: LocalStore) {
    this.local = local;
  }

  /**
   * 用域名识别应用
   */
  getAppFromHostName(hostname: string): string {
    if (this.local.app != null) {
      return this.local.app;
    }
    if (DOMAIN[hostname] != null) {
      return DOMAIN[hostname];
    }
    return process.env.DEFAULT_APP as string;
  }

  /** OAuth 登录页优先使用经过回跳地址校验的目标产品。 */
  getApp(): string {
    return this.getOAuthApp() ?? this.getAppFromHostName(location.hostname);
  }

  /** 返回登录接口使用的产品，普通登录保持产品域名优先。 */
  getLoginApp(): string {
    return this.getOAuthApp() ?? DOMAIN[location.hostname] ?? this.local.app ?? '';
  }

  getFavicon(app = this.getApp()): string {
    return `${CDN}/microapp/${this.getBrandApp(app)}_favicon.ico`;
  }

  /** 无独立品牌资源的工具产品借用经营分析样式，登录仍使用各自产品身份 */
  getBrandApp(app: string): string {
    return [App.HARNESS, App.PLAYGROUND].includes(app as App) ? App.MARKETING : app;
  }

  getAppName(app = this.getApp()): string {
    if (app === App.MARKETING) {
      return t('Marketing BI');
    }
    if (app === App.TRAINING) {
      return t('训练助手');
    }
    if (app === App.DETECTIVE) {
      return t('神访助手');
    }
    if (app === App.ADMIN) {
      return t('admin');
    }
    if (app === App.SHOTEYES) {
      return t('巡店助手');
    }
    if (app === App.SHIFTPLAN) {
      return t('排班助手');
    }
    if (app === App.HARNESS) {
      return t('harness');
    }
    if (app === App.PLAYGROUND) {
      return t('Playground');
    }
    return '';
  }

  getAppDescription(app = this.getApp()): string {
    if (app === App.MARKETING) {
      return t('经营分析 - 洞察餐饮门店经营数据，让营销效果清晰可见');
    }
    if (app === App.TRAINING) {
      return t('训练助手 - 连锁门店人才训练培养体系。');
    }
    if (app === App.DETECTIVE) {
      return t('神访助手 - 倾听来自客户的心声。');
    }
    if (app === App.ADMIN) {
      return t('admin');
    }
    if (app === App.SHOTEYES) {
      return t('巡店助手 - 专注连锁店标准化运营管理，重塑连锁总部和门店的连接，帮助企业提升连锁店一线服务团队执行力和运营管理水平。');
    }
    if (app === App.SHOTEYES) {
      return t('排班助手 - 帮助企业提升员工排班管理能力。');
    }
    return '';
  }

  getAppKeyword(): string {
    return '巡店助手,巡店系统,ShotEyes,Alphabets,字符科技,字小符科技,巡店,督导管理,连锁,标准化,门店运营管理,小程序,经营分析,训练助手,排班助手';
  }

  getPrimaryColor(app = this.getApp()): string {
    const brand = this.getBrandApp(app);
    if (brand === App.MARKETING) {
      return '#024FA2';
    }
    if (brand === App.TRAINING) {
      return '#00ADB5';
    }
    if (brand === App.SHOTEYES) {
      return '#E44134';
    }
    if (brand === App.SHIFTPLAN) {
      return '#E75B1B';
    }
    return '#E44134';
  }

  getLogo(app = this.getApp()): string {
    const brand = this.getBrandApp(app);
    if (brand === App.MARKETING) {
      return `${CONFIG.IMAGE_STATIC}/analyze_logo@2x.png`;
    }
    if (brand === App.TRAINING) {
      return `${CONFIG.IMAGE_STATIC}/training_logo@2x.png`;
    }
    if (brand === App.DETECTIVE) {
      return `${CONFIG.IMAGE_STATIC}/detective_logo@2x.png`;
    }
    if (brand === App.SHOTEYES) {
      return `${CONFIG.IMAGE_STATIC}/shoteyes_logo@2x.png`;
    }
    if (brand === App.SHIFTPLAN) {
      return `${CONFIG.IMAGE_STATIC}/shiftplan_logo@2x.png`;
    }
    if (brand === App.ADMIN) {
      return `${CONFIG.IMAGE_STATIC}/admin_logo@2x.png`;
    }
    return `${CONFIG.IMAGE_STATIC}/shoteyes_logo@2x.png`;
  }

  getFontFamily(): string[] {
    return [
      'PingFang SC',
      'Microsoft YaHei',
      '-apple-system',
      'BlinkMacSystemFont',
      'Segoe UI',
      'Roboto',
      'Helvetica Neue',
      'Arial',
      'Noto Sans',
      'sans-serif',
      'Apple Color Emoji',
      'Segoe UI Emoji',
      'Segoe UI Symbol',
      'Noto Color Emoji'
    ];
  }

  private getOAuthApp(): App | undefined {
    if (location.pathname !== '/login') {
      return;
    }
    const params = new URLSearchParams(location.search);
    const app = params.get('app');
    const redirect = params.get('redirect');
    const allowed = app === App.SHOTEYES || app === App.TRAINING || app === App.HARNESS
      || app === App.MARKETING || app === App.PLAYGROUND;
    if (!allowed || redirect == null) {
      return;
    }
    try {
      const redirectUrl = new URL(redirect);
      return redirectUrl.hostname === location.hostname
        && redirectUrl.pathname === '/api/oauth/authorize'
        ? app
        : undefined;
    } catch {
      return;
    }
  }

}
